import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  ChangeEvent,
} from 'react'

import { Modal, TooltipModal } from '@components/common/modal'
import { FlexContainer, YSpacing, Table, Loader } from '@components/common'
import {
  AutocompleteInput,
  Button,
  Checkbox,
  LabelInfo,
  TextInput,
} from '@components/common/form'
import AuthorizedDisplay from '@containers/common/auth/AuthorizedDisplay'
import { Label } from '@res/styledComponents/index'

import { HubspotFoodPartner, HubspotContact } from '@types'

interface FoodPartnerModalProps {
  currentHq: number
  close: () => void
  confirmationModal: { show: (val: { text: string }) => Promise<boolean> }
  checkFoodPartnerLinked: (id: string) => Promise<boolean>
  getHubspotFoodPartner: (id: string) => Promise<{
    foodPartner: HubspotFoodPartner
    contacts: HubspotContact[]
  } | null>
  newManualChef: () => void
  newChefFromHubspot: (
    fp: HubspotFoodPartner,
    c: HubspotContact[] | null,
    hq: number,
  ) => void
  loadChefs: (search: string) => Promise<any[]>
  selectChef: (id: string) => Promise<void>
}

const FoodPartnerModal: React.FC<FoodPartnerModalProps> = ({
  currentHq,
  close,
  confirmationModal,
  checkFoodPartnerLinked,
  getHubspotFoodPartner,
  newManualChef,
  newChefFromHubspot,
  loadChefs,
  selectChef,
}) => {
  const [foodPartnerId, setFoodPartnerId] = useState('')
  const [importContacts, setImportContacts] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isLinked, setIsLinked] = useState<boolean | null>(null)
  const [selectedFoodPartner, setSelectedFoodPartner] =
    useState<HubspotFoodPartner | null>(null)
  const [contacts, setContacts] = useState<HubspotContact[] | null>(null)
  const timer = useRef<NodeJS.Timeout | null>(null)

  const callAfterTimeout = useCallback(
    (func: () => void, delay = 1500) => {
      if (timer.current) {
        clearTimeout(timer.current)
      }
      timer.current = setTimeout(func, delay)
    },
    [timer],
  )

  const searchHubspotFoodPartner = useCallback(async () => {
    if (foodPartnerId.length === 0) {
      return
    }
    setLoading(true)
    const foodPartnerData = await getHubspotFoodPartner(foodPartnerId)
    if (foodPartnerData) {
      const { foodPartner, contacts } = foodPartnerData
      setSelectedFoodPartner(foodPartner)
      setContacts(contacts.length == 0 ? null : contacts)
      const linked = await checkFoodPartnerLinked(foodPartnerId)
      setIsLinked(linked)
    }
    setLoading(false)
  }, [foodPartnerId, getHubspotFoodPartner, checkFoodPartnerLinked])

  useEffect(() => {
    callAfterTimeout(searchHubspotFoodPartner, 1500)

    return () => {
      if (timer.current) {
        clearTimeout(timer.current)
      }
    }
  }, [foodPartnerId, searchHubspotFoodPartner, callAfterTimeout, timer])

  const onChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
    setFoodPartnerId((e.target as HTMLInputElement).value)
    setSelectedFoodPartner(null)
    setContacts(null)
    setIsLinked(null)
  }

  const onManualInput = async () => {
    const confirmManual = await confirmationModal.show({
      text: 'Are you sure you wish to enter a client manually? Tech team suggests importing clients from Hubspot.',
    })
    if (confirmManual) {
      newManualChef()
    }
  }

  const onSearchChefs = async ({ search }: { search: string }) => {
    const chefs = await loadChefs(search)
    if (chefs) {
      return chefs
    }
  }

  const onSelectExistingChef = async ({ id }: { id: string }) =>
    await selectChef(id)

  const renderExistingChefInput = () => (
    <div>
      <AutocompleteInput
        label="Select Existing Chef"
        displayAttribute="name"
        width="31%"
        loaderFunction={onSearchChefs}
        onSelect={(obj: { id: string }) => onSelectExistingChef({ id: obj.id })}
      />
    </div>
  )

  const onFoodPartnerSelect = async (foodPartner: HubspotFoodPartner) => {
    if (isLinked) {
      return
    }
    const confirmCorrectFP = await confirmationModal.show({
      text: `Are you sure you want to create a chef for ${foodPartner.partnerName}?`,
    })
    if (selectedFoodPartner && confirmCorrectFP) {
      const importedContacts = importContacts ? contacts : null
      newChefFromHubspot(foodPartner, importedContacts, currentHq)
    }
  }

  const renderSelectedFoodPartner = () => {
    if (!selectedFoodPartner) {
      return null
    }

    const { id, partnerName, metro, partnerType } = selectedFoodPartner
    const linked = isLinked === true

    return (
      <>
        <tr
          key={id}
          style={{
            cursor: linked ? 'not-allowed' : 'default',
            opacity: linked ? 0.5 : 1,
          }}
        >
          <td>{partnerName}</td>
          <td>{metro ? metro.join(', ') : 'N/A'}</td>
          <td>{partnerType ? partnerType.join(', ') : 'N/A'}</td>
        </tr>
        {linked && (
          <tr>
            <td colSpan={3} style={{ color: 'red', fontStyle: 'italic' }}>
              This food partner is already linked to an existing vendor in
              admin.
            </td>
          </tr>
        )}
      </>
    )
  }

  const renderContacts = () => {
    if (!importContacts || !contacts) {
      return null
    }

    return (
      <>
        <YSpacing height="20px" />
        <Label>Contacts</Label>
        <YSpacing height="10px" />
        <Table headings={['Name', 'Email', 'Phone']}>
          {contacts?.map(({ id, firstName, lastName, email, phone }) => (
            <tr key={id}>
              <td>{`${firstName} ${lastName}`}</td>
              <td>{email}</td>
              <td>{phone}</td>
            </tr>
          ))}
        </Table>
      </>
    )
  }

  return (
    <Modal
      width="1200px"
      title="Search Hubspot Food Partners"
      hideModal={close}
    >
      <FlexContainer flexDirection="row" alignItems="flex-start">
        <FlexContainer flexDirection="column" width="30%">
          <FlexContainer>
            <Label>Enter HubSpot Food Partner Record Number</Label>
            <TooltipModal
              unicode="&#9432;"
              width="400px"
              information="Please ensure that you have successfully created a food partner in hubspot & then search by food partner's record number."
              marginTop="18px"
            />
          </FlexContainer>
          <TextInput
            type="text"
            placeholder="Record Number"
            value={foodPartnerId}
            onChange={onChangeInput}
            width="200px"
          />
        </FlexContainer>
      </FlexContainer>
      <YSpacing height="10px" />
      <FlexContainer flexDirection="row">
        <Checkbox
          label="Import Contacts"
          checked={importContacts}
          onChange={() => setImportContacts(!importContacts)}
        />
      </FlexContainer>
      <YSpacing height="10px" />
      <FlexContainer flexDirection="column">
        {loading ? (
          <Loader />
        ) : (
          <Table headings={['Name', 'Metro', 'Partner Type']}>
            {renderSelectedFoodPartner()}
          </Table>
        )}
      </FlexContainer>
      {renderContacts()}
      {selectedFoodPartner && isLinked === false && (
        <>
          <YSpacing height="10px" />
          <Button
            label="Import Chef"
            onClick={() => onFoodPartnerSelect(selectedFoodPartner)}
          />
        </>
      )}
      <YSpacing height="10px" />
      <LabelInfo label={'-- OR --'} />
      <YSpacing height="10px" />
      {renderExistingChefInput()}
      <YSpacing height="30px" />

      {!selectedFoodPartner && (
        <AuthorizedDisplay roles={['master admin', 'chef recruiter']}>
          <YSpacing height="10px" />
          <FlexContainer
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <p style={{ color: 'red' }}>
              Only master admins and chef leads can manually create chefs.
            </p>
            <YSpacing height="10px" />
            <Button label="Manual Entry" onClick={onManualInput} />
          </FlexContainer>
        </AuthorizedDisplay>
      )}
    </Modal>
  )
}

export default FoodPartnerModal
